/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { functionsService, type FunctionIOField } from '../../services/functions.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate, useHasPermission } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { JOB_NAMES } from '../../constants/jobNames';
import ActiveTag from '../../components/common/active-tag';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { Field, Section } from '../../components/common/detail-view';
import FunctionTestDialog from './function-test-dialog';

/** Type-aware view of `configuration` — the keys each functionType actually uses,
 *  labelled, instead of a raw JSON blob. */
const configFields = (functionType?: string, cfg: Record<string, any> = {}) => {
  switch (functionType) {
    case 'IMPORT_PATH':
      return [
        { label: 'Module path', value: cfg.modulePath, mono: true },
        { label: 'Callable', value: cfg.callable, mono: true },
        // A row not yet migrated still carries the single string — flag it.
        cfg.importPath && { label: 'Import path (legacy — run migration)', value: cfg.importPath, mono: true, warn: true },
        cfg.parameterMapping && { label: 'Parameter mapping', value: cfg.parameterMapping, json: true },
      ];
    case 'REGISTERED_HANDLER':
      return [{ label: 'Handler key', value: cfg.handlerKey, mono: true }];
    case 'QUERY':
      return [
        { label: 'Collection', value: cfg.collection, mono: true },
        { label: 'Filter template', value: cfg.filterTemplate, json: true },
        { label: 'Projection', value: cfg.projection, json: true },
      ];
    case 'API_CALL':
      return [
        { label: 'Connection', value: cfg.connectionRef, mono: true },
        { label: 'Method', value: cfg.method },
        { label: 'Path', value: cfg.path, mono: true },
        { label: 'Body template', value: cfg.bodyTemplate, json: true },
      ];
    case 'EXPRESSION':
      return [{ label: 'Expression', value: cfg.expression, mono: true }];
    default:
      return [];
  }
};

const IOTable = ({ title, rows }: { title: string; rows?: FunctionIOField[] | null }) => (
  <div>
    <label className="text-xs text-ink-muted block mb-1">{title}</label>
    {rows?.length ? (
      <div className="flex flex-col gap-1">
        {rows.map((f) => (
          <div key={f.name} className="text-sm text-ink">
            <span className="font-mono">{f.name}</span>
            <span className="text-gray-400"> : {f.dataType}{f.isCollection ? '[]' : ''}{f.isRequired ? ' *' : ''}</span>
            {f.description ? <span className="text-gray-500"> — {f.description}</span> : null}
          </div>
        ))}
      </div>
    ) : <span className="text-sm text-gray-400">-</span>}
  </div>
);

const FunctionView = () => {
  const { id, isReady, navigateToList, navigateToEdit } = useSecureNavigation({ entity: 'function', basePath: '/functions' });
  const canUpdate = useCanUpdate(JOB_NAMES.FUNCTION_MASTER);
  const canTest = useHasPermission(PERMISSIONS.FUNCTION_MASTER_TEST);
  const [testOpen, setTestOpen] = useState(false);
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['function', id], queryFn: () => functionsService.getById(id!), enabled: isReady && !!id });

  if (isLoading) return <PageSkeleton />;
  if (isError || !data) return (
    <div className="p-4">
      <Message severity="error" text={extractErrorMessage(error, 'Function not found')} className="mb-4 w-full" />
      <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
    </div>
  );

  const cfgFields = configFields(data.functionType, (data.configuration as any) || {}).filter(Boolean) as any[];

  return (
    <div className="p-3 flex flex-col gap-3" data-testid="SCR-Function-View">
      <ScreenHeader icon="pi pi-code" title="Function Details" onBack={() => navigateToList()} />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <h2 className="text-base font-semibold text-ink font-mono">{data.functionCode}</h2>
            <Tag value={data.capability} severity={data.capability === 'EVAL_TARGET' ? 'info' : 'secondary'} className="text-xs px-1.5 py-0.5" />
            <Tag value={data.functionType} severity="secondary" className="text-xs px-1.5 py-0.5" />
            <ActiveTag row={data} />
          </div>
          {data.description && <p className="text-sm text-ink-muted max-w-3xl">{data.description}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canTest && !data.isDelete && (
            <Button label="Test" icon="pi pi-play" size="small" severity="secondary" outlined onClick={() => setTestOpen(true)} />
          )}
          {canUpdate && !data.isDelete && <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => id && navigateToEdit(id)} />}
        </div>
      </div>

      <Section title="Configuration">
        {cfgFields.length
          ? cfgFields.map((f) => <Field key={f.label} {...f} />)
          : <Field label="Configuration" value={data.configuration} json />}
        <Field label="Default timeout" value={data.defaultTimeoutSeconds != null ? `${data.defaultTimeoutSeconds}s` : null} />
      </Section>

      <Section title="I/O contract">
        <IOTable title="Input definition" rows={data.inputDefinition} />
        <IOTable title="Output definition" rows={data.outputDefinition} />
      </Section>

      <Section title="Details">
        <Field label="Function code" value={data.functionCode} mono />
        <Field label="Type" value={data.functionType} />
        <Field label="Capability" value={data.capability} />
        <Field label="Status" value={data.isDelete ? 'Deleted' : (data.isActive === false ? 'Inactive' : 'Active')} />
        <Field label="Project (appId)" value={data.appId ?? 'Global / shared'} mono={!!data.appId} />
      </Section>

      <AuditInfoCard
        createdAt={data.createdAt}
        updatedAt={data.updatedAt}
        createdBy={data.createdBy}
        updatedBy={data.updatedBy}
      />

      {testOpen && <FunctionTestDialog fn={data} visible={testOpen} onHide={() => setTestOpen(false)} />}
    </div>
  );
};

export default FunctionView;
